import { useParams, Navigate } from "react-router-dom";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import BlogDetailHero from "@/components/landing/blog/BlogDetailHero";
import BlogContent from "@/components/landing/blog/BlogContent";
import BlogRelatedArticles from "@/components/landing/blog/BlogRelatedArticles";
import CTASection from "@/components/landing/CTASection";
import { getBlogPostBySlug } from "@/data/blogPosts";
import {
  StructuredDataHead,
  LLMMetadataHead,
  organizationSchema,
  createWebPageSchema,
  createArticleSchema,
} from "@/hooks/useStructuredData";
import { useLanguage } from "@/hooks/useLanguage";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language } = useLanguage();
  const post = slug ? getBlogPostBySlug(slug, language) : undefined;

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const metadataTitle = post.seo?.title ?? `${post.title} | Chathero Blog`;
  const metadataDescription = post.seo?.description ?? post.excerpt;
  const canonicalUrl = post.seo
    ? `https://chathero-ai.lovable.app${post.seo.path}`
    : `https://chathero.ai/blog/${post.slug}`;

  return (
    <div className="min-h-screen bg-background">
      <StructuredDataHead schemas={[
        organizationSchema,
        createWebPageSchema(
          metadataTitle,
          metadataDescription,
          `/blog/${post.slug}`,
          [
            { name: "Home", url: "/" },
            { name: "Blog", url: "/blog" },
            { name: post.title, url: `/blog/${post.slug}` },
          ]
        ),
        { ...createArticleSchema({
          title: post.title,
          description: post.excerpt,
          slug: post.slug,
          publishDate: post.publishDate,
          author: post.author,
          category: post.category,
        }), ...(post.seo ? { url: canonicalUrl, mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl } } : {}) },
      ]} />
      <LLMMetadataHead
        title={metadataTitle}
        description={metadataDescription}
        keywords={post.seo?.keywords ?? post.tags.join(", ")}
        canonicalUrl={canonicalUrl}
        articlePublishedTime={post.publishDate}
        articleAuthor={post.author.name}
        aiCategory={`Blog, ${post.category}`}
        aiEntityType="Blog Article"
      />
      <Header />
      <main>
        <BlogDetailHero post={post} />
        <BlogContent post={post} />
        <BlogRelatedArticles currentSlug={post.slug} />
        {!post.hideGlobalCTA && <CTASection />}
      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;
